import moment from 'moment';
import { ForemanJobDetail } from '@ups/xplat/api/dto';
import { findIndividualTimeEntryTargetEmployee } from './foreman-individual-entry-employee.utils';
import { hasForemanLaborDraftEdits } from './foreman-labor-draft.utils';

/**
 * Portable ITE weekly summary grouping (ADO #30133).
 * Platform views (web Kendo / NS Foreman Toolbox) should call this; do not reimplement week boundary.
 */

/**
 * Pay-type hours for the weekly summary.
 * Labels: ST (regular), OT, PT shown as DT, VAC, HOL, SICK, TRV.
 */
export interface WeeklyPayTypeHours {
  st: number;
  ot: number;
  pt: number;
  vac: number;
  hol: number;
  trv: number;
  sick: number;
}

export interface WeeklySummaryWorkOrderNode {
  foremanPhaseID: string;
  phaseName: string;
  notes: string;
  hours: WeeklyPayTypeHours;
  hasUnsubmittedDrafts: boolean;
  /** 1 = Day, 2 = Night. The summary lists both; job rows stay shift-scoped. */
  shift: number;
  shiftName: string;
}

export interface WeeklySummaryJobNode {
  foremanJobID: string;
  jobName: string;
  vpJobID: string;
  hours: WeeklyPayTypeHours;
  hasUnsubmittedDrafts: boolean;
  workOrders: WeeklySummaryWorkOrderNode[];
}

export interface WeeklySummaryDayNode {
  /** Local calendar day (start of day). */
  date: Date;
  /** MM/DD/YYYY key used by ITE save paths. */
  dateKey: string;
  hours: WeeklyPayTypeHours;
  hasUnsubmittedDrafts: boolean;
  jobs: WeeklySummaryJobNode[];
}

export interface WeeklySummaryModel {
  weekStart: Date;
  weekEnd: Date;
  hours: WeeklyPayTypeHours;
  hasUnsubmittedDrafts: boolean;
  days: WeeklySummaryDayNode[];
}

/**
 * Platform-agnostic live row override (unsaved edits for one day).
 * Web `IndividualWorkOrderRow` / mobile equivalents should satisfy this shape.
 */
export interface WeeklySummaryLiveWorkOrderRow {
  foremanPhaseID: string;
  phaseName: string;
  description?: string;
  st: number | null;
  ot: number | null;
  pt: number | null;
  vac: number | null;
  hol: number | null;
  trv: number | null;
  sick: number | null;
  notes: string;
  hasUnsubmittedDrafts?: boolean;
  shift?: number | null;
}

/**
 * Platform-agnostic live job panel for the currently edited day.
 * Web `IndividualJobPanel` is structurally compatible.
 */
export interface WeeklySummaryLiveJobPanel {
  job: ForemanJobDetail;
  workOrderRows: WeeklySummaryLiveWorkOrderRow[];
}

export function emptyPayTypeHours(): WeeklyPayTypeHours {
  return { st: 0, ot: 0, pt: 0, vac: 0, hol: 0, trv: 0, sick: 0 };
}

/** Night is 2; everything else, including 0/null, is Day — same as the ITE picker. */
export function weeklySummaryShift(shift: number | null | undefined): number {
  return Number(shift) === 2 ? 2 : 1;
}

export function weeklySummaryShiftName(
  shift: number | null | undefined
): string {
  return weeklySummaryShift(shift) === 2 ? 'Night' : 'Day';
}

function phaseShiftKey(phaseId: string, shift: number): string {
  return `${phaseId}|${shift}`;
}

export function totalPayTypeHours(h: WeeklyPayTypeHours): number {
  return h.st + h.ot + h.pt + h.vac + h.hol + h.trv + h.sick;
}

export function addPayTypeHours(
  a: WeeklyPayTypeHours,
  b: WeeklyPayTypeHours
): WeeklyPayTypeHours {
  return {
    st: a.st + b.st,
    ot: a.ot + b.ot,
    pt: a.pt + b.pt,
    vac: a.vac + b.vac,
    hol: a.hol + b.hol,
    trv: a.trv + b.trv,
    sick: a.sick + b.sick,
  };
}

/**
 * Timekeeping week containing `date`: Monday 00:00:00.000 through Sunday 23:59:59.999 local.
 *
 * Algorithm matches FTM `weekRangeForDate` (setDate, then setHours) and the
 * Monday–Sunday rule documented on backend DateTimeCommon.
 *
 * Why a local copy instead of an import: the FTM helper lives under
 * `apps/sparta/web-sparta/...` and cannot be imported from this xplat library.
 * `@ups/xplat/utils` `getWeekRange` is NOT a safe source — its start-of-week
 * builds from `new Date()` (today's month/year) and is broken for non-current
 * anchors. Do not cite or call it from ITE.
 */
export function iteWeekRangeForDate(date: Date): {
  weekStart: Date;
  weekEnd: Date;
} {
  const day = date.getDay(); // 0=Sun … 6=Sat
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const weekStart = new Date(date);
  // Order matches FTM weekRangeForDate: adjust calendar day first, then clamp time.
  weekStart.setDate(date.getDate() + diffToMonday);
  weekStart.setHours(0, 0, 0, 0);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  weekEnd.setHours(23, 59, 59, 999);
  return { weekStart, weekEnd };
}

/** Inclusive local-calendar membership of `date` in [weekStart, weekEnd]. */
export function isDateInIteWeek(
  date: Date,
  weekStart: Date,
  weekEnd: Date
): boolean {
  const t = startOfLocalDay(date).getTime();
  return (
    t >= startOfLocalDay(weekStart).getTime() &&
    t <= startOfLocalDay(weekEnd).getTime()
  );
}

function startOfLocalDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function dateKeyOf(date: Date): string {
  return moment(date).format('MM/DD/YYYY');
}

/**
 * Map a Sparta `foremanEarnCode` onto its summary bucket, or null when the code
 * contributes no hours.
 *
 * Exported because it is the single source of truth for "which earn codes are
 * hours at all". Callers that only need a yes/no should use
 * {@link isPayTypeEarnCode} rather than keeping a second list: the mobile ITE
 * summary held its own `['ST','OT','PT']` set, which silently dropped VAC/SICK/
 * HOL/TRV from the day total, the submit gate and the submit payload.
 */
export function earnCodeToPayTypeField(
  code: string | null | undefined
): keyof WeeklyPayTypeHours | null {
  switch ((code ?? '').toUpperCase()) {
    case 'ST':
      return 'st';
    case 'OT':
      return 'ot';
    case 'PT':
      return 'pt';
    case 'VAC':
      return 'vac';
    case 'HOL':
      return 'hol';
    case 'TRV':
      return 'trv';
    case 'SICK':
      return 'sick';
    default:
      // PD (per diem) and unknown codes do not contribute hours to the summary.
      return null;
  }
}

/** True when `code` carries hours — worked (ST/OT/PT/TRV) or leave (VAC/HOL/SICK). */
export function isPayTypeEarnCode(code: string | null | undefined): boolean {
  return earnCodeToPayTypeField(code) !== null;
}

function hoursFromRow(row: WeeklySummaryLiveWorkOrderRow): WeeklyPayTypeHours {
  return {
    st: row.st ?? 0,
    ot: row.ot ?? 0,
    pt: row.pt ?? 0,
    vac: row.vac ?? 0,
    hol: row.hol ?? 0,
    trv: row.trv ?? 0,
    sick: row.sick ?? 0,
  };
}

function hasSummaryContent(hours: WeeklyPayTypeHours, notes: string): boolean {
  return totalPayTypeHours(hours) > 0 || !!(notes && notes.trim());
}

/**
 * Build a read-only weekly summary tree for ITE (web or mobile).
 *
 * Saved labors on each job's target employee are the base. When `livePanels`
 * is provided for the currently edited day, those rows replace saved labors
 * for that day so the summary reflects unsaved edits.
 */
export function buildWeeklySummary(args: {
  anchorDate: Date;
  jobs: ForemanJobDetail[];
  spartaEmployeeId: string | null;
  /**
   * Ignored since WI #30588 (Tim, 2026-08-17): the weekly summary is shift-agnostic,
   * so the week total and its day/job/WO breakdown include every shift the employee
   * worked. Retained (optional) for caller-signature stability and to make re-scoping
   * to a single shift a one-line change if the product decision ever reverses.
   */
  selectedShift?: number;
  livePanels?: WeeklySummaryLiveJobPanel[];
  liveDate?: Date;
}): WeeklySummaryModel {
  const { weekStart, weekEnd } = iteWeekRangeForDate(args.anchorDate);
  const liveDateKey =
    args.liveDate && args.livePanels ? dateKeyOf(args.liveDate) : null;
  // Jobs the live panels speak for. A panel is the authority for its own job on
  // the edited day (it carries unsaved edits AND cleared rows), but it says
  // nothing about a job that has no panel — the page shows a subset of the job
  // list (a panel the user removed to declutter, or the non-billable-only panel
  // set on a future leave day). Replacing the WHOLE day with the panel set zeroed
  // out those jobs' already-saved hours, so the day and week read low against
  // Time Manager for as long as the panel stayed off screen.
  const livePanelJobIds = new Set(
    (args.livePanels ?? [])
      .map((p) => p.job?.foremanJobID || p.job?.jobID)
      .filter((id): id is string => !!id)
  );

  interface PhaseBucket {
    foremanPhaseID: string;
    phaseName: string;
    notes: string;
    hours: WeeklyPayTypeHours;
    hasUnsubmittedDrafts: boolean;
    shift: number;
  }
  interface JobBucket {
    jobName: string;
    vpJobID: string;
    phases: Map<string, PhaseBucket>;
  }
  const days = new Map<string, Map<string, JobBucket>>();

  const ensureDay = (key: string) => {
    if (!days.has(key)) days.set(key, new Map());
    return days.get(key)!;
  };

  const ensureJob = (
    dayMap: Map<string, JobBucket>,
    job: ForemanJobDetail
  ): JobBucket => {
    const id = job.foremanJobID || job.jobID;
    let bucket = dayMap.get(id);
    if (!bucket) {
      bucket = {
        jobName: job.name || job.vpJobID || id,
        vpJobID: job.vpJobID ?? '',
        phases: new Map(),
      };
      dayMap.set(id, bucket);
    }
    return bucket;
  };

  const addLaborHours = (
    phase: PhaseBucket,
    field: keyof WeeklyPayTypeHours,
    hours: number
  ) => {
    phase.hours[field] += hours;
  };

  // 1) Saved labors across the week (skip live day if panels override it).
  for (const job of args.jobs ?? []) {
    const target = findIndividualTimeEntryTargetEmployee(
      job,
      args.spartaEmployeeId
    );
    for (const labor of target?.labors ?? []) {
      if (labor.bActive === false) continue;
      // Absence is not hours. Every other ITE consumer excludes absent rows by
      // default (`isMatchingIndividualLabor`, which the job-row badge and the
      // submit filter both call), and this builder feeds the day total AND the
      // submit gate, so counting them here would let an absent row inflate TH
      // and flip the >= 16 / >= 20 / < 24 thresholds. No traced path currently
      // produces `isAbsent` with hours, so this closes a divergence rather than
      // a live symptom, and it keeps the two filters from drifting again.
      if (labor.isAbsent) continue;
      // The phase ID stays REQUIRED: it is this builder's phase-bucket key, and
      // the only phase-less producer is `setAbsent`, whose row carries no hours.
      // Dropping the guard would render those as a phantom work-order node.
      if (!labor.dateWorked || !labor.foremanPhaseID) continue;
      if (!isDateInIteWeek(labor.dateWorked, weekStart, weekEnd)) continue;

      const key = dateKeyOf(labor.dateWorked);
      if (
        liveDateKey &&
        key === liveDateKey &&
        livePanelJobIds.has(job.foremanJobID || job.jobID)
      ) {
        continue;
      }

      // No shift filter (WI #30588, Tim 2026-08-17): the weekly summary is
      // shift-agnostic. It counts every shift the employee worked so the week
      // total matches Time Manager (which is shift-agnostic) and the live-panel
      // day (which never filtered). A prior day must not drop from the week total
      // just because it was a different shift than the page's current selection.
      const field = earnCodeToPayTypeField(labor.foremanEarnCode);
      if (!field) {
        continue;
      }

      const dayMap = ensureDay(key);
      const jobBucket = ensureJob(dayMap, job);
      const shift = weeklySummaryShift(labor.shift);
      const bucketKey = phaseShiftKey(labor.foremanPhaseID, shift);
      let phase = jobBucket.phases.get(bucketKey);
      if (!phase) {
        const wo = (job.workOrders ?? []).find(
          (w) => w.foremanPhaseID === labor.foremanPhaseID
        );
        phase = {
          foremanPhaseID: labor.foremanPhaseID,
          phaseName: wo?.phaseName || wo?.description || labor.foremanPhaseID,
          notes: '',
          hours: emptyPayTypeHours(),
          hasUnsubmittedDrafts: false,
          shift,
        };
        jobBucket.phases.set(bucketKey, phase);
      }
      addLaborHours(phase, field, labor.hours ?? 0);
      phase.hasUnsubmittedDrafts ||= hasForemanLaborDraftEdits(labor);
      if (labor.foremanNotes?.trim()) {
        if (!phase.notes) {
          phase.notes = labor.foremanNotes.trim();
        } else if (!phase.notes.includes(labor.foremanNotes.trim())) {
          phase.notes = `${phase.notes}; ${labor.foremanNotes.trim()}`;
        }
      }
    }
  }

  // 2) Live panel override for the edited day.
  if (args.livePanels && args.liveDate && liveDateKey) {
    if (isDateInIteWeek(args.liveDate, weekStart, weekEnd)) {
      const dayMap = ensureDay(liveDateKey);
      for (const panel of args.livePanels) {
        const job = panel.job;
        const jobBucket = ensureJob(dayMap, job);
        for (const row of panel.workOrderRows ?? []) {
          const hours = hoursFromRow(row);
          const notes = (row.notes ?? '').trim();
          if (!hasSummaryContent(hours, notes)) continue;

          const shift = weeklySummaryShift(row.shift);
          jobBucket.phases.set(phaseShiftKey(row.foremanPhaseID, shift), {
            foremanPhaseID: row.foremanPhaseID,
            phaseName: row.phaseName || row.description || row.foremanPhaseID,
            notes,
            hours,
            hasUnsubmittedDrafts: row.hasUnsubmittedDrafts === true,
            shift,
          });
        }
      }
    }
  }

  // 3) Materialize sorted tree (only nodes with content).
  const dayNodes: WeeklySummaryDayNode[] = [];
  const weekHours = emptyPayTypeHours();

  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    d.setHours(0, 0, 0, 0);
    const key = dateKeyOf(d);
    const jobMap = days.get(key);
    if (!jobMap || jobMap.size === 0) continue;

    const dayHours = emptyPayTypeHours();
    let dayHasUnsubmittedDrafts = false;
    const jobs: WeeklySummaryJobNode[] = [];

    for (const [foremanJobID, jobBucket] of jobMap) {
      const jobHours = emptyPayTypeHours();
      let jobHasUnsubmittedDrafts = false;
      const workOrders: WeeklySummaryWorkOrderNode[] = [];

      for (const phase of jobBucket.phases.values()) {
        if (!hasSummaryContent(phase.hours, phase.notes)) continue;
        workOrders.push({
          foremanPhaseID: phase.foremanPhaseID,
          phaseName: phase.phaseName,
          notes: phase.notes,
          hours: { ...phase.hours },
          hasUnsubmittedDrafts: phase.hasUnsubmittedDrafts,
          shift: phase.shift,
          shiftName: weeklySummaryShiftName(phase.shift),
        });
        jobHasUnsubmittedDrafts ||= phase.hasUnsubmittedDrafts;
        Object.assign(jobHours, addPayTypeHours(jobHours, phase.hours));
      }

      if (workOrders.length === 0) continue;

      workOrders.sort((a, b) => {
        const byName = a.phaseName.localeCompare(b.phaseName, undefined, {
          sensitivity: 'base',
        });
        return byName !== 0 ? byName : a.shift - b.shift;
      });

      jobs.push({
        foremanJobID,
        jobName: jobBucket.jobName,
        vpJobID: jobBucket.vpJobID,
        hours: jobHours,
        hasUnsubmittedDrafts: jobHasUnsubmittedDrafts,
        workOrders,
      });
      dayHasUnsubmittedDrafts ||= jobHasUnsubmittedDrafts;
      Object.assign(dayHours, addPayTypeHours(dayHours, jobHours));
    }

    if (jobs.length === 0) continue;

    jobs.sort((a, b) =>
      a.jobName.localeCompare(b.jobName, undefined, { sensitivity: 'base' })
    );

    dayNodes.push({
      date: d,
      dateKey: key,
      hours: dayHours,
      hasUnsubmittedDrafts: dayHasUnsubmittedDrafts,
      jobs,
    });
    Object.assign(weekHours, addPayTypeHours(weekHours, dayHours));
  }

  return {
    weekStart,
    weekEnd,
    hours: weekHours,
    hasUnsubmittedDrafts: dayNodes.some((day) => day.hasUnsubmittedDrafts),
    days: dayNodes,
  };
}
